import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import { interactiveFloorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { Icon } from '@components/icon/Icon';
import { WideView } from '@components/wideView/WideView';
import {
  getComboAwareActiveListingIds,
  getComboAwareTableIds,
} from '@shared/floorPlans/comboFloorPlanTables';
import { useResetZoom } from '@shared/hooks/useResetZoom';
import type { FloorPlanTableData } from '@shared/types/floorPlans';
import cx from 'classnames';
import { useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import typography from '~styles/typography.scss';
import { useConciergeAvailabilityContext } from './ConciergeAvailabilityContext';
import styles from './InteractiveConciergeFloorPlan.scss';

export const HAS_SEEN_PULSING_TABLE_GUIDANCE =
  'peak-has-seen-pulsing-table-guidance';

interface InteractiveConciergeFloorPlanProps {
  backgroundSrc?: string;
  tables: FloorPlanTableData[];
  multiSelect?: boolean;
  darkBackground?: boolean;
  className?: string;
}

export const InteractiveConciergeFloorPlan = ({
  backgroundSrc,
  tables,
  multiSelect = false,
  darkBackground = false,
  className,
}: InteractiveConciergeFloorPlanProps) => {
  const hasSeenPulsingTableGuidance =
    localStorage.getItem(HAS_SEEN_PULSING_TABLE_GUIDANCE) === 'true';
  const zoomRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [hoveredFloorPlanTableId, setHoveredFloorPlanTableId] = useState<
    string | null
  >(null);
  const {
    clearSelectedAvailability,
    selectedAvailability,
    selectedFloorPlanListingIds,
    selectedFloorPlanTableIds,
    setSelectedFloorPlanListingIds,
    toggleSelectedFloorPlanTableId,
    setSelectedFloorPlanTableIds,
    expandedAvailabilityListingIds,
    clearSelectedFloorPlanTables,
    selectedGuestCount,
  } = useConciergeAvailabilityContext();

  useResetZoom(zoomRef, [selectedAvailability], !!selectedAvailability);

  let highlightedListingIds = selectedFloorPlanListingIds;
  const selectedBestAvailableAvailability =
    selectedAvailability?.flaggedWhenSelected &&
    selectedAvailability?.isBestAvailable;
  if (selectedAvailability && !selectedBestAvailableAvailability) {
    highlightedListingIds = [selectedAvailability.listing.id];
  }

  const calculateIsSelectable = (floorPlanTable: FloorPlanTableData) =>
    multiSelect ||
    floorPlanTable.listings.some((listing) =>
      expandedAvailabilityListingIds.includes(listing.id),
    );

  const calculateIsHighlighted = (
    floorPlanTable: FloorPlanTableData,
  ): boolean =>
    (calculateIsSelectable(floorPlanTable) &&
      floorPlanTable.listings
        .filter((listing) =>
          expandedAvailabilityListingIds.includes(listing.id),
        )
        .some((listing) => highlightedListingIds.includes(listing.id))) ||
    selectedFloorPlanTableIds.includes(floorPlanTable.id);

  const handleTableOnClick = (floorPlanTable: FloorPlanTableData) => {
    if (multiSelect) {
      toggleSelectedFloorPlanTableId(floorPlanTable.id);
      if (!hasSeenPulsingTableGuidance) {
        window.localStorage.setItem(HAS_SEEN_PULSING_TABLE_GUIDANCE, 'true');
      }
    } else {
      if (calculateIsSelectable(floorPlanTable)) {
        clearSelectedAvailability();

        const activeListingIds = floorPlanTable.listings
          .filter((listing) =>
            expandedAvailabilityListingIds.includes(listing.id),
          )
          .map(({ id }) => id);

        setSelectedFloorPlanListingIds(
          getComboAwareActiveListingIds(
            tables,
            floorPlanTable,
            activeListingIds,
            selectedGuestCount,
          ),
        );
        setSelectedFloorPlanTableIds(
          getComboAwareTableIds(
            tables,
            floorPlanTable,
            activeListingIds,
            selectedGuestCount,
          ),
        );

        if (!hasSeenPulsingTableGuidance) {
          window.localStorage.setItem(HAS_SEEN_PULSING_TABLE_GUIDANCE, 'true');
        }
      }
    }
  };

  const clearFloorPlanSelection = () => {
    clearSelectedFloorPlanTables();
  };

  const handleTableOnMouseEnter = (floorPlanTable: FloorPlanTableData) => {
    setHoveredFloorPlanTableId(floorPlanTable.id);
  };

  const handleTableOnMouseLeave = () => {
    setHoveredFloorPlanTableId(null);
  };

  const hoveredTable = tables.find(
    (table) => table.id === hoveredFloorPlanTableId,
  );
  const hoveredComboTableIds =
    !multiSelect && hoveredTable && calculateIsSelectable(hoveredTable)
      ? getComboAwareTableIds(
          tables,
          hoveredTable,
          hoveredTable.listings
            .filter((listing) =>
              expandedAvailabilityListingIds.includes(listing.id),
            )
            .map(({ id }) => id),
          selectedGuestCount,
        )
      : [];

  const calculateIsComboHovered = (floorPlanTable: FloorPlanTableData) =>
    hoveredComboTableIds.includes(floorPlanTable.id);

  const generateTableWithPulse = () => {
    const firstSelectableTable = tables.find((table) =>
      calculateIsSelectable(table),
    );

    let tableWithPulse = tables;
    if (firstSelectableTable && !hasSeenPulsingTableGuidance) {
      tableWithPulse = tables.map((table) => {
        if (table.id === firstSelectableTable.id) {
          return { ...table, pulse: true };
        }
        return table;
      });
    }
    return tableWithPulse;
  };

  const floorPlanTablesRenderer: FloorPlanTablesRenderer =
    interactiveFloorPlanTablesRendererFactory<FloorPlanTableData>({
      calculateIsComboHovered,
      calculateIsHighlighted,
      calculateIsSelectable,
      handleTableOnClick,
      handleTableOnMouseEnter,
      handleTableOnMouseLeave,
      tables: generateTableWithPulse(),
      showName: true,
      highlightIcon: false,
    });

  return (
    <div
      className={cx({
        [styles.floorPlanContainer]: true,
        [styles.darkBackground]: darkBackground,
      })}
    >
      <div className={styles.floorPlan}>
        <FloorPlan
          backgroundSrc={backgroundSrc}
          className={className}
          floorPlanTablesRenderer={floorPlanTablesRenderer}
          handleBackgroundOnClick={() => {
            // If we're multi-selecting a user might accidentally click on the background
            // which would annoyingly clear their selection.
            if (multiSelect) return;
            clearFloorPlanSelection();
          }}
          ref={zoomRef}
        />
      </div>
      {!!selectedFloorPlanListingIds.length && (
        <WideView
          narrowElement={
            <button
              aria-label="Clear floor plan selection"
              className={styles.clearSelectionButton}
              data-testid="clear-floor-plan-selection-button-narrow"
              onClick={clearFloorPlanSelection}
            >
              <Icon className={styles.resetIcon} name="reset" />
            </button>
          }
          wideElement={
            <button
              className={cx(typography.t1, styles.clearSelectionButton)}
              data-testid="clear-floor-plan-selection-button-wide"
              onClick={clearFloorPlanSelection}
            >
              <Icon className={styles.resetIcon} name="reset" /> Clear floor
              plan selection
            </button>
          }
        />
      )}
    </div>
  );
};
